import { useEffect, useState } from "react";

import { IEvent, IHost } from "@Interfaces";
import { getEarlyBirdObj } from "@Modules/eventPage/utils/earlyBird";
import { evaluateIsGSTIncluded } from "@Modules/eventPage/utils/evaluateIsGSTIncluded";
import { getAreIntlPaymentsEnabled } from "@Modules/eventPage/utils/getAreIntlPaymentsEnabled";
import { getBillingData } from "@Modules/eventPage/utils/getBillingData";
import getEventCurrencyForDisplaying from "@Modules/eventPage/utils/getEventCurrencyForDisplaying";
import { getPricePerHead } from "@Modules/eventPage/utils/getInitialPrice";
import { getSelectedUpsellOfferingsData } from "@Modules/eventPage/utils/getSelectedUpsellListingsData";
import {
    GST_STATUS,
    QUERY_PARAMS,
    SELECTORS,
    STORAGE_KEYS,
    getCurrencyString,
    roundUp,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { getURLSearchParamsObj } from "@Utils/urls";
import { useSelector } from "react-redux";
import { URL_SEARCH_PARAMS } from "repoV2/constants/urls&routing/urlParams";
import { isInstallmentPaymentType } from "repoV2/utils/common/listing";
import { setLocalStorageItem } from "repoV2/utils/common/storage/getterAndSetters";
import { getHideEarlyBird } from "../../EarlyBird/utils/getHideEarlyBird";
import { useInstalmentsDetailsContext } from "../../PaymentPlan/utils/InstalmentsDetailsContext";
import { useRenewBookingFormContext } from "../../RenewBookingForm/utils/RenewBookingFormContext";
import { getUpsellListingsForEventId } from "../../UpsellListings/utils/getUpsellListingsForEventId";
import { IPaymentSummary } from "../IPaymentSummary";
import { getHidePaymentSummary } from "./getHidePaymentSummary";

// TODO @mayank: get backdrop + click functionality implemented in mobile render

const usePaymentSummaryData = ({
    isMobile,
    urlQueryParams,
    onToggleIsPaymentSummaryExpanded,
    disablePayment,
    showExpandedBill = false,
}: IPaymentSummary.IProps & {
    onToggleIsPaymentSummaryExpanded?: (
        isPaymentSummaryExpanded: boolean
    ) => void;
}) => {
    const { isRenewBooking } = useRenewBookingFormContext();
    const { eventData, event } = useEventDataOfSelectedEvent();
    const host: IHost.IStore = useSelector(SELECTORS.host);
    const hostData = host?.dataHost?.profile_data;
    const areIntlPaymentsEnabled: boolean = getAreIntlPaymentsEnabled({
        host,
        eventData,
    });

    const communityId = getURLSearchParamsObj().get(
        URL_SEARCH_PARAMS.COMMUNITY_ID
    );

    const earlyBirdObj: IEvent.IEarlyBird | null = getEarlyBirdObj({
        eventData,
        disablePayment,
        isRenewBooking,
        urlQueryParams,
        areIntlPaymentsEnabled,
    });

    const { eventCurrencySymbol } = getEventCurrencyForDisplaying({
        eventData,
    });

    const { selectedPaymentType, instalments } = useInstalmentsDetailsContext();
    const [initialPayment] = instalments || [];

    const pricePerHead = getPricePerHead({
        eventData,
        initialInstalment: initialPayment,
        selectedPaymentType,
    });
    const isInternationalCreator = eventData?.is_international_creator;
    const isGSTRegistered = eventData?.gst_state !== GST_STATUS.INAPPLICABLE;

    const {
        paymentInfo: {
            numberOfCustomers,
            otherListings: selectedUpsellOfferingsQuantities, // quantities of listings selected from upsell
        },
    } = event;

    const upsellListings = getUpsellListingsForEventId({
        eventId: eventData.uuid,
        event,
    });

    const additionalListings = getSelectedUpsellOfferingsData({
        upsellListings,
        selectedUpsellOfferingsQuantities,
    });

    const isGSTIncluded: boolean = evaluateIsGSTIncluded({ eventData });

    const [isPaymentSummaryExpanded, setIsPaymentSummaryExpanded] =
        useState(showExpandedBill);

    const { paymentInfoForMainOffering, paymentInfos, billingData } =
        getBillingData({
            event,
            eventData,
            initialInstalment: initialPayment,
            selectedPaymentType,
            earlyBirdObj,
        });

    const {
        // Boolean flags for certain conditions
        flags: { pretendCgstInapplicable, billingDisplayFormatState },
        // Absolute numbers for price values
        priceValues: {
            unitPrice, // Base unit price
            cgstExclusiveUnitPrice,
        },
        // Scaling factors
        factors: {
            billingMultiplier: numberOfItems, // Number of instances of items being bought for this SKU
        },
    } = paymentInfoForMainOffering;

    const toggleIsPaymentSummaryExpanded = () => {
        const newVal = !isPaymentSummaryExpanded;
        setIsPaymentSummaryExpanded(newVal);
        if (onToggleIsPaymentSummaryExpanded)
            onToggleIsPaymentSummaryExpanded(newVal);
    };

    const showEarlyBirdClock = !getHideEarlyBird({
        disablePayment,
        earlyBirdObj,
        urlQueryParams,
        eventData,
    });
    const customerHasSelectedItemsForSummary = unitPrice * numberOfItems > 0;

    // TODO: Better storage mechanism
    useEffect(() => {
        const currencyString: string = getCurrencyString(eventCurrencySymbol);

        // The presence of the status key indicates the page to be open in a post-payment state where we do not want
        //  to overwrite the pre-payment value that will have information about quantity, number, upsell, and discount

        if (!window.location.href?.includes(QUERY_PARAMS.TRANSACTION_STATUS)) {
            setLocalStorageItem(
                STORAGE_KEYS.TOTAL_AMOUNT,
                billingData?.totalPrice?.toString()
            );
            setLocalStorageItem(
                STORAGE_KEYS.CURRENCY_SYMBOL,
                currencyString.toString()
            );
        }
    }, [billingData.totalPrice]);
    // If you want to implement swiping opening and close the overlay, use this:
    // https://stackoverflow.com/questions/40463173/swipe-effect-in-react-js

    // The review details section will not be expandable if pretendCgstInapplicable
    // is true, so renaming the variable before being passed as a prop
    const disablePaymentSummaryExpanding = pretendCgstInapplicable;

    // TODO: Move calculation to payment utils
    const totalPrice: number = roundUp(
        disablePaymentSummaryExpanding
            ? Math.max(billingData.initialPrice - billingData.discountPrice, 0)
            : billingData.totalPrice
    );

    /**
     * `true` when base offering + upsell offerings are selected
     */
    const multipleOfferingsSelected = paymentInfos.length > 1;

    const _pricePerHeader =
        pricePerHead /
        (paymentInfoForMainOffering.flags.separateCgst
            ? paymentInfoForMainOffering.factors.gstScalingFactor
            : 1);

    const commonPaymentPriceAndExpandedBillProps = {
        earlyBirdObj,
        pricePerHead: _pricePerHeader,
        multipleOfferingsSelected,
    };
    const evaluatePaymentPriceProps: IPaymentSummary.IEvaluatePaymentPrice = {
        numberOfItems: 1,
        eventType: eventData.type,
        cgstExclusiveUnitPrice,
        unitPrice,
        billingDisplayFormatState,
        ...commonPaymentPriceAndExpandedBillProps,
    };
    const expandedBillProps: IPaymentSummary.IExpandedBillProps = {
        isMobile,
        isRebooking: isRenewBooking,
        numberOfCustomers,
        paymentInfos,
        basePaymentValues: paymentInfoForMainOffering,
        eventType: eventData.type,
        isGSTIncluded,
        eventCurrencySymbol,
        eventTitle: eventData.title,
        otherListings: selectedUpsellOfferingsQuantities,
        additionalListings,
        billingData,
        isInternationalCreator,
        shippingText: eventData?.metadata?.merchandise?.shipping_text,
        toggleIsPaymentSummaryExpanded,
        isPaymentSummaryExpanded,
        isGSTRegistered,
        ...commonPaymentPriceAndExpandedBillProps,
    };

    const expandButtonProps: IPaymentSummary.IExpandButtonProps = {
        isMobile,
        expandButtonCtaText:
            hostData?.event_payment_summary_expand_cta_text || "Review Details",
        disablePaymentSummaryExpanding,
        customerHasSelectedItemsForSummary,
        showEarlyBirdClock,
        earlyBirdTime: earlyBirdObj?.time,
        urlQueryParams,
    };

    const billSummaryProps: IPaymentSummary.IBillSummaryProps = {
        eventCurrencySymbol,
        toggleIsPaymentSummaryExpanded,
        totalPrice,
        showStrikedOutPrice:
            showEarlyBirdClock &&
            customerHasSelectedItemsForSummary &&
            !communityId &&
            !isInstallmentPaymentType(selectedPaymentType), // disable earlyBird offer when user opts for installments payment plan
        strikedOutPrice: billingData.totalPriceWOEarlyBird,
        billSummaryLabel: "Total Payable Amount",
        showBillSummaryConditionalLabel: disablePaymentSummaryExpanding,
        billSummaryConditionalLabel: "(Exclusive of Taxes)",
        expandButtonProps,
        disablePaymentSummaryExpanding,
    };

    return {
        hideComponent: getHidePaymentSummary({ disablePayment, eventData }),
        expandedBillProps,
        billSummaryProps,
        isPaymentSummaryExpanded,
        evaluatePaymentPriceProps,
    };
};

export default usePaymentSummaryData;
